import { useState } from "react";
import { siteInfo, stats, faqs } from "@/mocks/site";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOMeta from "@/components/base/SEOMeta";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus("success");
    setContactForm({ name: "", email: "", message: "" });
  };

  return (
    <>
      <SEOMeta
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、平台使命与核心价值观。${siteInfo.name}致力于为蘑菇tv创作者提供最优质的资源汇聚服务。`}
        keywords={`关于蘑菇tv成品人,蘑菇tv成品人介绍,蘑菇tv成品人品牌,蘑菇tv资源平台介绍`}
        canonicalUrl="https://mogutv.com/about"
        ogTitle={`关于我们 - ${siteInfo.name}`}
        ogDescription={`了解${siteInfo.name}的品牌故事、平台使命与核心价值观。`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: `关于我们 - ${siteInfo.name}`,
            description: `了解${siteInfo.name}的品牌故事、平台使命与核心价值观。`,
            url: "https://mogutv.com/about",
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: siteInfo.name,
            url: "https://mogutv.com",
            description: siteInfo.description,
            email: siteInfo.email,
            telephone: `+86-${siteInfo.phone}`,
            address: {
              "@type": "PostalAddress",
              addressLocality: "北京市",
              addressRegion: "朝阳区",
              streetAddress: "望京科技园A座1208室",
              addressCountry: "CN",
            },
            foundingDate: String(siteInfo.foundingYear),
          },
        ]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />

            {/* Hero */}
            <section className="text-center py-12 md:py-16">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
                关于{siteInfo.name}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                {siteInfo.description}
              </p>
            </section>

            {/* Brand Story */}
            <section className="mb-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                    <p>
                      <strong className="text-foreground-900">{siteInfo.name}</strong>创立于{siteInfo.foundingYear}年，源于一群热爱蘑菇tv内容创作的资深创作者的共同愿景——打造一个真正为蘑菇tv成品人服务的精品资源汇聚平台。
                    </p>
                    <p>
                      在蘑菇tv内容生态蓬勃发展的浪潮中，我们发现创作者们面临着一个共同的痛点：优质资源分散、信息获取效率低、缺乏系统化的创作指导。于是，{siteInfo.name}应运而生。
                    </p>
                    <p>
                      经过多年的深耕与发展，{siteInfo.name}已经从一个简单的资源分享站点，成长为覆盖创作教程、灵感素材、工具推荐、行业资讯等多品类的综合性内容平台。我们始终秉持<strong className="text-foreground-900">"让创作更简单"</strong>的理念，致力于为每一位蘑菇tv成品人提供最优质、最高效的服务体验。
                    </p>
                    <p>
                      如今，{siteInfo.name}已经汇聚了超过{stats[0].value}注册创作者，累计收录{stats[1].value}成品资源，用户好评率高达{stats[3].value}。这些数字背后，是每一位蘑菇tv成品人对我们的信任与支持。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20team%20collaboration%2C%20warm%20ambient%20lighting%2C%20diverse%20creative%20professionals%20working%20together%2C%20content%20creation%20studio%20atmosphere%2C%20editorial%20photography%2C%20clean%20modern%20interior%2C%20positive%20energy%2C%20creative%20workspace%20with%20plants%20and%20natural%20light&width=800&height=600&seq=about-brand-mogutv&orientation=landscape"
                    alt={`${siteInfo.name} 品牌故事`}
                    title={`${siteInfo.name} 品牌故事`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Core Values */}
            <section className="mb-14 py-12 bg-background-100 rounded-2xl px-6 md:px-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 text-center">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: "ri-shield-star-line",
                    title: "品质至上",
                    desc: "严格筛选每一份成品资源，确保内容的原创性、实用性和高质量标准。我们宁缺毋滥，只为蘑菇tv成品人提供真正有价值的资源。",
                    color: "primary",
                  },
                  {
                    icon: "ri-user-heart-line",
                    title: "创作者为本",
                    desc: "一切以蘑菇tv创作者的需求为中心，不断优化平台功能和服务体验。倾听每一位成品人的声音，持续改进产品。",
                    color: "accent",
                  },
                  {
                    icon: "ri-lightbulb-flash-line",
                    title: "创新驱动",
                    desc: "紧跟内容创作行业的前沿趋势，持续引入新技术、新工具和新理念。帮助蘑菇tv成品人永远走在创作潮流的最前沿。",
                    color: "secondary",
                  },
                ].map((value) => {
                  const colorStyles = {
                    primary: { iconBg: "bg-primary-100", text: "text-primary-600" },
                    accent: { iconBg: "bg-accent-100", text: "text-accent-600" },
                    secondary: { iconBg: "bg-secondary-100", text: "text-secondary-600" },
                  };
                  const cs = colorStyles[value.color as keyof typeof colorStyles] || colorStyles.primary;
                  return (
                    <div key={value.title} className="text-center p-6">
                      <div className={`w-14 h-14 mx-auto flex items-center justify-center rounded-xl mb-4 ${cs.iconBg}`}>
                        <i className={`${value.icon} text-2xl ${cs.text}`}></i>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground-900 mb-2">{value.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Platform Stats */}
            <section className="mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 text-center">
                平台数据
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center p-6 bg-background-100 rounded-xl border border-background-200/70">
                    <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-14">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Contact info */}
                <div className="space-y-5">
                  {[
                    { icon: "ri-mail-line", label: "电子邮箱", value: siteInfo.email },
                    { icon: "ri-phone-line", label: "联系电话", value: siteInfo.phone },
                    { icon: "ri-map-pin-line", label: "公司地址", value: siteInfo.address },
                    { icon: "ri-wechat-line", label: "微信公众号", value: siteInfo.wechat },
                    { icon: "ri-qq-line", label: "QQ群号", value: siteInfo.qq },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4 p-4 bg-background-100 rounded-xl border border-background-200/70">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className={`${item.icon} text-lg text-primary-600`}></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-500 mb-0.5">{item.label}</div>
                        <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Contact form */}
                <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>
                  {formStatus === "success" ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                        <i className="ri-check-line text-2xl text-primary-600"></i>
                      </div>
                      <h4 className="text-lg font-semibold text-foreground-900 mb-2">消息已发送!</h4>
                      <p className="text-sm text-foreground-600">感谢您的留言，我们会在1-2个工作日内回复您。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-background-300 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                          placeholder="请输入您的留言内容（最多500字）"
                        />
                        <div className="text-xs text-foreground-500 mt-1 text-right">{contactForm.message.length}/500</div>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 cursor-pointer text-sm whitespace-nowrap"
                      >
                        发送消息
                        <i className="ri-send-plane-line ml-2"></i>
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 text-center">
                常见问题
              </h2>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqs.slice(0, 4).map((faq, index) => (
                  <div key={index} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer.slice(0, 200)}...</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}